import { useRef, useState } from "react";
import type {
  AttackCard,
  ImView,
  ProcedureCard,
  PublicSessionState,
  ServerMessage,
  Stage,
  TeamRole,
} from "../types";

const STAGE_LABEL: Record<Stage, string> = {
  initialCompromise: "Initial compromise",
  pivotEscalate: "Pivot / escalate",
  c2Exfil: "C2 / exfil",
  persistence: "Persistence",
};

const ROLE_LABEL: Record<TeamRole, string> = { soc: "SOC", "ir-lead": "IR Lead", comms: "Comms" };
const ROLE_TIP: Record<TeamRole, string> = {
  soc: "You read the logs and play the procedures. Watch every cooldown. Tell IR Lead what you see, fast.",
  "ir-lead": "You drive the timeline. You decide what the team plays next. You brief the IM on what you've found.",
  comms: "You handle injects. CEO calls, legal, press. Keep the team informed of pressure changes.",
};
import { CardView } from "../components/CardView";
import { AttackChain } from "../components/AttackChain";
import { Log } from "../components/Log";
import { CardDetail } from "../components/CardDetail";
import { Chat } from "../components/Chat";
import { useDialog } from "../components/useDialog";

interface Props {
  state: PublicSessionState;
  lastRoll?: Extract<ServerMessage, { type: "rollResult" }>;
  isIm: boolean;
  isPlayer: boolean;
  myPlayerId?: string;
  onPlay: (cardId: string, predictStage?: Stage) => void;
  onFireInject: (teamId: string) => void;
  onChat: (text: string) => void;
  onUsePower: () => void;
}

export function Table({ state, lastRoll, isIm, isPlayer, myPlayerId, onPlay, onFireInject, onChat, onUsePower }: Props) {
  const team = state.myTeam;
  const me = team?.players.find((p) => p.id === myPlayerId);
  const myRole: TeamRole | undefined = me?.teamRole;
  const [filter, setFilter] = useState<"all" | "established" | "hand">("all");
  const [detail, setDetail] = useState<{ card: ProcedureCard | AttackCard; kind: "procedure" | "stage" } | null>(null);
  const [briefOpen, setBriefOpen] = useState(false);
  // The declaration bet: an optional stage call armed before playing a card.
  // Consumed by the next play, then reset to no-call.
  const [predict, setPredict] = useState<Stage | undefined>(undefined);
  const play = (cardId: string) => {
    onPlay(cardId, predict);
    setPredict(undefined);
  };
  const undeclaredStages = (Object.keys(STAGE_LABEL) as Stage[]).filter(
    (s) => !team?.found.some((f) => f.stage === s),
  );

  return (
    <>
      <div className="row">
        <div className="col grow" style={{ minWidth: 0 }}>
          <section className="panel">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <h2 style={{ margin: 0 }}>Attack chain</h2>
              {(state.scenarioPremise || state.scenarioOpeningNarration) && (
                <button onClick={() => setBriefOpen(true)} style={{ fontSize: 11 }}>
                  📖 Re-read brief
                </button>
              )}
            </div>
            <AttackChain myTeam={team} imView={state.imView} />
          </section>

          {team && (
            <section className="panel">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 12,
                  flexWrap: "wrap",
                  gap: 10,
                }}
              >
                <h2 style={{ margin: 0 }}>
                  Your procedures — turn {team.turnsTaken + 1} / {state.maxTurns}
                  {team.pendingModifier ? (
                    <span
                      style={{
                        marginLeft: 10,
                        fontSize: 11,
                        fontFamily: "var(--font-mono)",
                        color: "var(--color-accent)",
                        border: "1px solid var(--color-accent)",
                        padding: "2px 6px",
                        verticalAlign: "middle",
                      }}
                    >
                      +{team.pendingModifier} next roll
                    </span>
                  ) : null}
                </h2>
                <div style={{ display: "flex", gap: 6 }}>
                  <button className={filter === "all" ? "primary" : ""} onClick={() => setFilter("all")}>All</button>
                  <button className={filter === "established" ? "primary" : ""} onClick={() => setFilter("established")}>Established</button>
                  <button className={filter === "hand" ? "primary" : ""} onClick={() => setFilter("hand")}>In hand</button>
                </div>
              </div>
              {isPlayer && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    flexWrap: "wrap",
                    gap: 6,
                    marginBottom: 12,
                    fontSize: 11,
                  }}
                >
                  <span style={{ fontFamily: "var(--font-mono)", color: "var(--color-text-muted)", marginRight: 4 }}>
                    Call the stage before you play · +2 if right, free if wrong
                  </span>
                  <button className={predict === undefined ? "primary" : ""} onClick={() => setPredict(undefined)} style={{ fontSize: 11 }}>
                    No call
                  </button>
                  {undeclaredStages.map((s) => (
                    <button
                      key={s}
                      className={predict === s ? "primary" : ""}
                      onClick={() => setPredict(predict === s ? undefined : s)}
                      style={{ fontSize: 11 }}
                    >
                      {STAGE_LABEL[s]}
                    </button>
                  ))}
                </div>
              )}
              <div className="cards">
                {(filter !== "hand" ? team.established : []).map((c) => (
                  <CardView
                    key={`est-${c.id}`}
                    card={c}
                    established
                    cooldown={team.cooldowns[c.id] ?? 0}
                    disabled={!isPlayer}
                    onClick={() => isPlayer && play(c.id)}
                    onDetail={() => setDetail({ card: c, kind: "procedure" })}
                  />
                ))}
                {(filter !== "established" ? team.hand : []).map((c) => (
                  <CardView
                    key={`hand-${c.id}`}
                    card={c}
                    cooldown={team.cooldowns[c.id] ?? 0}
                    disabled={!isPlayer}
                    onClick={() => isPlayer && play(c.id)}
                    onDetail={() => setDetail({ card: c, kind: "procedure" })}
                  />
                ))}
              </div>
            </section>
          )}

          {team && (
            <section className="panel">
              <h2>Team chat</h2>
              <Chat log={state.log} myTeamId={team.id} onSend={onChat} />
            </section>
          )}
        </div>

        <div className="col sidebar">
          {myRole && (
            <section className="panel" style={{ borderTop: "2px solid var(--color-accent)" }}>
              <h2>You're playing as · {ROLE_LABEL[myRole]}</h2>
              <p style={{ fontSize: 12, lineHeight: 1.55, margin: 0, color: "var(--color-text-muted)" }}>
                {ROLE_TIP[myRole]}
              </p>
              {isPlayer && team && (
                <RolePower
                  role={myRole}
                  spent={!!team.powersUsed?.[myRole]}
                  lastRollFailed={!!lastRoll && !lastRoll.success && lastRoll.teamId === team.id}
                  shieldArmed={!!team.injectShieldArmed}
                  freePlayArmed={!!team.freePlayArmed}
                  onUse={onUsePower}
                />
              )}
            </section>
          )}

          <section className="panel">
            <h2>Race</h2>
            <div className="lb">
              <div className="head">Team</div>
              <div className="head">Found</div>
              <div className="head">Turn</div>
              <div className="head">Pressure</div>
              {team && (
                <div className="row-team">
                  <div>★ {team.name}</div>
                  <div>{team.found.length}/4</div>
                  <div>{team.turnsTaken}/{state.maxTurns}</div>
                  <div><span className="pressure" role="img" aria-label={`pressure ${team.pressure} of 10`}><span style={{ width: `${team.pressure * 10}%` }} /></span></div>
                </div>
              )}
              {state.otherTeams.map((t) => (
                <div className="row-team" key={t.id}>
                  <div>{t.name}</div>
                  <div>{t.found.length}/4</div>
                  <div>{t.turnsTaken}/{state.maxTurns}</div>
                  <div><span className="pressure" role="img" aria-label={`pressure ${t.pressure} of 10`}><span style={{ width: `${t.pressure * 10}%` }} /></span></div>
                </div>
              ))}
            </div>
          </section>

          <section className="panel">
            <h2>Last roll</h2>
            <div aria-live="polite" aria-atomic="true">
            {lastRoll ? (
              <div className="dice">
                <div className={`face ${lastRoll.success ? "success" : "fail"}`}>{lastRoll.rolled}</div>
                <div style={{ fontSize: 12 }}>
                  + {lastRoll.modifier} = <strong>{lastRoll.rolled + lastRoll.modifier}</strong>
                  <div style={{ color: "var(--color-text-muted)", fontSize: 11 }}>
                    {lastRoll.success
                      ? lastRoll.revealed
                        ? `revealed ${lastRoll.revealed.stage}`
                        : "hit"
                      : lastRoll.wouldHaveDetected === undefined
                        ? "miss"
                        : lastRoll.wouldHaveDetected
                          ? "miss — right instrument, the dice said no"
                          : "miss — it was never going to see this one"}
                  </div>
                  {lastRoll.predictedStage && (
                    <div style={{ color: lastRoll.predictionCorrect ? "var(--color-good)" : "var(--color-text-subtle)", fontSize: 11 }}>
                      called {STAGE_LABEL[lastRoll.predictedStage]} — {lastRoll.predictionCorrect ? "+2, right call" : "not this one"}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>—</div>
            )}
            </div>
          </section>

          <section className="panel">
            <h2>Activity log</h2>
            <Log entries={state.log.filter((l) => l.kind !== "chat")} />
          </section>

          {isIm && state.imView && <ImControls imView={state.imView} onFireInject={onFireInject} onCardDetail={(c) => setDetail({ card: c, kind: "stage" })} />}
        </div>
      </div>

      {detail && (
        <CardDetail
          card={detail.card}
          kind={detail.kind}
          onClose={() => setDetail(null)}
        />
      )}

      {briefOpen && (
        <BriefModal
          scenarioName={state.scenarioName}
          premise={state.scenarioPremise}
          openingNarration={state.scenarioOpeningNarration}
          onClose={() => setBriefOpen(false)}
        />
      )}
    </>
  );
}

const POWER_LABEL: Record<TeamRole, string> = {
  soc: "Re-roll the last failed roll",
  comms: "Cancel the next inject",
  "ir-lead": "Clear a free play — no turn cost",
};

/** One power per role per team per game. The server enforces it; this shows
 *  it, including armed state for the two powers that wait to trigger. */
function RolePower({
  role,
  spent,
  lastRollFailed,
  shieldArmed,
  freePlayArmed,
  onUse,
}: {
  role: TeamRole;
  spent: boolean;
  lastRollFailed: boolean;
  shieldArmed: boolean;
  freePlayArmed: boolean;
  onUse: () => void;
}) {
  const armed = (role === "comms" && shieldArmed) || (role === "ir-lead" && freePlayArmed);
  const disabled = spent || armed || (role === "soc" && !lastRollFailed);
  return (
    <div style={{ marginTop: 10 }}>
      <button className={disabled ? "" : "primary"} disabled={disabled} onClick={onUse} style={{ fontSize: 11, width: "100%" }}>
        {POWER_LABEL[role]}
        {spent && !armed ? " — used" : ""}
        {armed ? " — armed" : ""}
      </button>
      <div style={{ fontSize: 10, color: "var(--color-text-subtle)", marginTop: 4, fontFamily: "var(--font-mono)" }}>
        one use per role per game
        {role === "soc" && !spent && !lastRollFailed ? " · needs a failed roll" : ""}
      </div>
    </div>
  );
}

function BriefModal({
  scenarioName,
  premise,
  openingNarration,
  onClose,
}: {
  scenarioName: string;
  premise?: string;
  openingNarration?: string;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialog(dialogRef, onClose);
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={`${scenarioName} — scenario brief`}
        style={{ maxWidth: 640 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-body">
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              letterSpacing: "0.18em",
              color: "var(--color-accent)",
              textTransform: "uppercase",
              marginBottom: 8,
            }}
          >
            Scenario brief
          </div>
          <h2 style={{ margin: "0 0 14px", fontSize: 22 }}>{scenarioName}</h2>
          {premise && (
            <p style={{ fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap", marginBottom: 14 }}>
              {premise}
            </p>
          )}
          {openingNarration && (
            <>
              <h3 style={{ marginTop: 4, marginBottom: 6 }}>Opening narration</h3>
              <p
                style={{
                  fontSize: 12.5,
                  lineHeight: 1.6,
                  color: "var(--color-text-muted)",
                  fontStyle: "italic",
                  whiteSpace: "pre-wrap",
                  borderLeft: "2px solid var(--color-accent)",
                  paddingLeft: 12,
                }}
              >
                {openingNarration}
              </p>
            </>
          )}
          <div style={{ marginTop: 22, display: "flex", justifyContent: "flex-end" }}>
            <button onClick={onClose}>Close — Esc</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ImVoiceoverPanel({ imView }: { imView: ImView }) {
  const [tab, setTab] = useState<"chars" | "beats" | "brief">("chars");
  return (
    <section className="panel">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 6 }}>
        <h2 style={{ margin: 0 }}>IM — Voiceover</h2>
        <div style={{ display: "flex", gap: 6 }}>
          <button className={tab === "chars" ? "primary" : ""} onClick={() => setTab("chars")}>Cast</button>
          <button className={tab === "beats" ? "primary" : ""} onClick={() => setTab("beats")}>Beats</button>
          <button className={tab === "brief" ? "primary" : ""} onClick={() => setTab("brief")}>Brief</button>
        </div>
      </div>

      {tab === "chars" && (
        <>
          {imView.characters.length === 0 && (
            <div style={{ fontSize: 12, color: "var(--color-text-subtle)" }}>
              No scripted characters — random scenario.
            </div>
          )}
          {imView.characters.map((c) => (
            <div key={c.name} className="voiceover-quote">
              <span className="who">
                {c.name} · {c.role}
              </span>
              <div style={{ fontSize: 11, color: "var(--color-text-subtle)", fontStyle: "normal", marginBottom: 6 }}>
                {c.voice}
              </div>
              {c.stockLines.map((line, i) => (
                <div key={i} style={{ marginBottom: 4 }}>"{line}"</div>
              ))}
            </div>
          ))}
        </>
      )}

      {tab === "beats" && (
        <>
          {imView.injectHooks.length === 0 && (
            <div style={{ fontSize: 12, color: "var(--color-text-subtle)" }}>
              No scripted beats — injects fire automatically.
            </div>
          )}
          {imView.injectHooks
            .slice()
            .sort((a, b) => a.turn - b.turn)
            .map((h, i) => (
              <div key={i} className="voiceover-quote">
                <span className="who">
                  Turn {h.turn} · {h.injectId}
                </span>
                {h.voiceover}
              </div>
            ))}
        </>
      )}

      {tab === "brief" && (
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, lineHeight: 1.6, whiteSpace: "pre-wrap", color: "var(--color-text)" }}>
          {imView.imBriefingNotes || "No IM briefing notes for this scenario."}
        </div>
      )}
    </section>
  );
}

function ImControls({
  imView,
  onFireInject,
  onCardDetail,
}: {
  imView: ImView;
  onFireInject: (teamId: string) => void;
  onCardDetail: (c: AttackCard) => void;
}) {
  return (
    <>
      <section className="panel">
        <h2>IM — Hidden chain</h2>
        <div className="chain" style={{ gridTemplateColumns: "1fr" }}>
          {imView.attackChain.map((c) => (
            <div
              key={c.id}
              className="stage revealed"
              onClick={() => onCardDetail(c)}
              style={{ cursor: "pointer" }}
            >
              <div className="label">{c.stage}</div>
              <div className="name">{c.name}</div>
            </div>
          ))}
        </div>
      </section>

      <ImVoiceoverPanel imView={imView} />

      <section className="panel">
        <h2>IM — Team rosters</h2>
        <div className="im-rosters">
          {imView.teams.map((t) => (
            <div className="team" key={t.id}>
              <div className="tname">{t.name}</div>
              <div className="meta">
                turn {t.turnsTaken} · found {t.foundCount}/4 · pressure {t.pressure}
              </div>
              {t.players.length === 0 ? (
                <div style={{ fontSize: 11, color: "var(--color-text-subtle)" }}>
                  no players yet
                </div>
              ) : (
                t.players.map((p) => (
                  <div key={p.id} className={`player ${p.online ? "" : "offline"}`}>
                    <span className="dot" /> {p.handle}
                  </div>
                ))
              )}
              <button
                style={{ marginTop: 10, width: "100%", fontSize: 11 }}
                onClick={() => onFireInject(t.id)}
              >
                Fire inject
              </button>
            </div>
          ))}
        </div>
        <div
          style={{
            fontSize: 11,
            color: "var(--color-text-subtle)",
            marginTop: 12,
            fontFamily: "var(--font-mono)",
          }}
        >
          Pending injects: {imView.pendingInjects}
        </div>
      </section>
    </>
  );
}
